import { EventDescription, Pricings } from './types/event'

interface TicketCategoryQuantity {
  category: string
  amount: number
}

interface PricingsRequestParams {
  dateId: Ref<string | undefined>
  quantity: Ref<number>
  isPrivate: Ref<boolean | undefined>
  categoryQuantities?: Ref<Record<string, number>>
  type?: Ref<'EVENT' | 'GIFT_CARD'>
}

export const useFetchPricings = (
  eventId: Ref<string>,
  {
    // params to override the default params
    dateId,
    quantity,
    isPrivate,
    categoryQuantities,
    type,
  }: PricingsRequestParams
) => {
  const config = useRuntimeConfig()
  const { $i18n } = useNuxtApp()
  const { locale: ISOLocale } = $i18n
  const locale = convertISO2Locale(ISOLocale.value)
  const localeBCP47 = convertLocaleIntoBCP47(ISOLocale.value)

  const url = `v1/store/events/${eventId.value}/pricings`

  // Build ticket_categories array from categoryQuantities or default to ADULT
  let ticketCategories: TicketCategoryQuantity[] = []

  if (
    categoryQuantities?.value &&
    Object.keys(categoryQuantities.value).length > 0
  ) {
    // Use category breakdown if provided
    ticketCategories = Object.entries(categoryQuantities.value)
      .filter(([_, amount]) => amount > 0)
      .map(([category, amount]) => ({
        category,
        amount,
      }))
  }

  // If no categories or all are 0, default to ADULT with the total quantity
  if (ticketCategories.length <= 1 && quantity.value > 0) {
    ticketCategories = [
      {
        category: 'ADULT',
        amount: quantity.value,
      },
    ]
  }

  const body = {
    // don't send event_id (date id) for kit and special giftcard events
    ...(dateId.value && { event_id: dateId.value }),
    private: isPrivate.value,
    ticket_categories: ticketCategories,
    ...(type?.value === 'GIFT_CARD' ? { type: 'GIFT_CARD' } : {}),
  }

  return $fetch<Pricings>(url, {
    baseURL: config.public.apiBaseURL,
    body,
    method: 'POST',
    headers: {
      'Accept-Language': `${localeBCP47},${locale}`,
    },
  })
}

export const useEventPricings = (
  event: Ref<EventDescription | undefined>,
  {
    dateId = ref(undefined),
    quantity,
    isPrivate,
    categoryQuantities,
    type,
  }: PricingsRequestParams
) => {
  const pricings = ref<Record<string, Pricings>>({})
  const eventId = computed(() => eventGetters.getId(event.value))
  const loading = ref(true)
  const { pricingKey } = useEventPricingsGetters(pricings, {
    dateId,
    quantity,
    event,
  })

  const fetchPricings = async () => {
    // Capture the pricing key at the start of the request
    // This ensures we store prices under the correct key even if quantities change during the request
    const requestPricingKey = pricingKey.value
    loading.value = true

    try {
      const pricingsResponse = await useFetchPricings(eventId, {
        dateId,
        quantity,
        isPrivate,
        categoryQuantities,
        ...(type?.value === 'GIFT_CARD' ? { type: ref('GIFT_CARD') } : {}),
      })

      if (!pricingsResponse) {
        throw new Error('No pricings response')
      }

      // Set prices in cache under the key that was valid when request started
      // This ensures prices are stored correctly even if quantities changed during request
      pricings.value = {
        ...pricings.value,
        [requestPricingKey]: pricingsResponse,
      }

      // Wait for next tick to ensure reactivity has updated
      await nextTick()

      // Set loading to false after prices are set and reactivity has updated
      // This ensures prices are available when loadingPricings becomes false
      loading.value = false
    } catch (error) {
      loading.value = false
      console.error(error)
    }
  }

  return {
    pricings,
    fetchPricings,
    loading,
  }
}
